import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { yardApis } from "@/features/relayYardControl/services/yardsApis";
import type { YardMove } from "@/features/relayYardControl/types/moves";
import { shouldUpdateBasedOnVersion } from "@/utils/versionUtils";

export interface YardMovesState {
  moves: Record<string, YardMove>;
}

const initialState: YardMovesState = { moves: {} };

const yardMovesSlice = createSlice({
  name: "yardMoves",
  initialState,
  reducers: {
    updateMove: (state, action: PayloadAction<YardMove>) => {
      const incoming = action.payload;
      if (!incoming?.moveId) return;
      const existing = state.moves[incoming.moveId];
      // Drop stale WS frames. Mirrors yardDetectionData/utils:233 and
      // ryteDataSlice — default to "0" so a first real WS update always wins
      // over REST-seeded entries that lack a version.
      if (
        existing &&
        !shouldUpdateBasedOnVersion(existing.datapathViewVersion, incoming.datapathViewVersion)
      ) {
        return;
      }
      state.moves[incoming.moveId] = incoming;
    },
    deleteMove: (state, action: PayloadAction<{ moveId: string }>) => {
      const { moveId } = action.payload;
      if (moveId) {
        delete state.moves[moveId];
      }
    },
    clearAllYardMoves: (state) => {
      state.moves = {};
    },
  },
  extraReducers: (builder) => {
    // Hydrates the slice from REST. Invoked automatically (no manual
    // dispatch) when the `getYardMoves` RTK Query request fulfills —
    // mirrors the `getYardVisits` matcher in yardDetectionData/slice.ts.
    // After hydration, individual WS frames update entries via the
    // `updateMove` action above.
    //
    // REST moves carry no `datapathViewVersion`, so we seed "0";
    // `shouldUpdateBasedOnVersion` treats "0" as legacy, letting the
    // first WS frame (which carries a real version) always win.
    builder.addMatcher(
      yardApis.endpoints.getYardMoves.matchFulfilled,
      (state, { payload }: { payload: { moves?: YardMove[] } | undefined }) => {
        state.moves = {};
        (payload?.moves ?? []).forEach((m) => {
          if (!m?.moveId) return;
          state.moves[m.moveId] = {
            ...m,
            datapathViewVersion: m.datapathViewVersion || "0",
          };
        });
      }
    );
  },
});

export const { updateMove, deleteMove, clearAllYardMoves } = yardMovesSlice.actions;
export default yardMovesSlice.reducer;
